import React, { useEffect, useState } from "react";

import TextField from "@mui/material/TextField";

/**
 * Campo que só COMETE quando o professor termina de digitar.
 *
 * A granularidade importa: cada edição manual vira um turno na conversa, e um
 * turno por tecla encheria o fio de ruído e faria uma chamada por caractere.
 * Commit em blur (ou Enter, quando é uma linha) dá exatamente um turno por
 * alteração — que é a unidade que o professor tem na cabeça.
 *
 * Reverte para o valor do servidor quando ele muda por fora: se outro turno
 * reescreveu o campo, o que está na tela deixa de valer.
 */
interface Props {
  id?: string;
  valor: string;
  onComitar: (novo: string) => void;
  label?: string;
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
  minRows?: number;
  maxRows?: number;
  numero?: boolean;
  tipo?: React.HTMLInputTypeAttribute;
  disabled?: boolean;
  fullWidth?: boolean;
  largura?: number;
  variant?: "standard" | "outlined" | "filled";
  /** Em campos visuais de uma linha que podem quebrar, Enter confirma. */
  confirmarComEnter?: boolean;
  /** Chamado a cada tecla, para feedback imediato sem commit. */
  onDigitar?: (parcial: string) => void;
}

export default function CampoEditavel({
  id,
  valor,
  onComitar,
  label,
  placeholder,
  multiline = false,
  rows,
  minRows,
  maxRows,
  numero = false,
  tipo,
  disabled = false,
  fullWidth = false,
  largura,
  variant = "outlined",
  confirmarComEnter = false,
  onDigitar,
}: Props) {
  const [texto, setTexto] = useState(valor);

  useEffect(() => {
    setTexto(valor);
  }, [valor]);

  const comitar = () => {
    if (texto === valor) return;
    onComitar(texto);
  };

  return (
    <TextField
      id={id}
      size="small"
      variant={variant}
      label={label}
      placeholder={placeholder}
      value={texto}
      disabled={disabled}
      multiline={multiline}
      rows={rows}
      minRows={minRows}
      maxRows={maxRows}
      fullWidth={fullWidth}
      type={tipo ?? (numero ? "number" : "text")}
      sx={largura ? { width: largura } : undefined}
      onChange={(e) => {
        setTexto(e.target.value);
        onDigitar?.(e.target.value);
      }}
      onBlur={comitar}
      onKeyDown={(e) => {
        if (
          e.key === "Enter" &&
          (!multiline || (confirmarComEnter && !e.shiftKey))
        ) {
          e.preventDefault();
          (e.target as HTMLInputElement).blur();
        }
        if (e.key === "Escape") {
          setTexto(valor);
          onDigitar?.(valor);
        }
      }}
    />
  );
}
